import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import QRCode from 'qrcode';
import jsPDF from 'jspdf';

const Users = () => {
    const [users, setUsers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [qrModalOpen, setQrModalOpen] = useState(false);
    const [qrDataUrl, setQrDataUrl] = useState('');
    const [qrUser, setQrUser] = useState(null);
    const [isDownloadingQRs, setIsDownloadingQRs] = useState(false);
    const [currentUser, setCurrentUser] = useState({
        name: '', last_name: '', identification: '', email: '', city: '', country: '', role: 'user', status: 'active'
    });
    const [isEditing, setIsEditing] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [regFields, setRegFields] = useState({
        identification: true, name: true, last_name: true, email: true, city: true, country: true
    });
    const [visualConfig, setVisualConfig] = useState(null);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [includeLogoInQR, setIncludeLogoInQR] = useState(true);

    useEffect(() => {
        fetchConfigs();
    }, []);

    useEffect(() => {
        fetchUsers();
    }, [searchQuery]);

    useEffect(() => {
        setSelectedUsers(users.map(u => u.id));
    }, [users]);

    const fetchConfigs = async () => {
        try {
            const [regRes, visRes] = await Promise.all([
                api.get('/configs/registration_fields'),
                api.get('/configs/visual_identity')
            ]);
            if (regRes.data) setRegFields(regRes.data);
            if (visRes.data) setVisualConfig(visRes.data);
        } catch (error) {
            console.error("Error fetching configs", error);
        }
    };

    const fetchUsers = async () => {
        setIsLoading(true);
        try {
            const response = await api.get(`/users?q=${searchQuery}`);
            if (!searchQuery) {
                console.log("Usuarios cargados (Sin filtros):", response.data);
            }
            setUsers(response.data);
        } catch (error) {
            console.error("Error fetching users", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await api.put(`/users/${currentUser.id}`, currentUser);
            } else {
                // Registration endpoint usually handles creation
                await api.post('/auth/register', currentUser);
            }
            fetchUsers();
            closeModal();
        } catch (error) {
            console.error("Error saving user", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar este usuario?")) {
            try {
                await api.delete(`/users/${id}`);
                fetchUsers();
            } catch (error) {
                console.error("Error deleting", error);
            }
        }
    };

    const openModal = (user = null) => {
        if (user) {
            setCurrentUser(user);
            setIsEditing(true);
        } else {
            setCurrentUser({
                name: '', last_name: '', identification: '', email: '', city: '', country: '', role: 'user', status: 'active'
            });
            setIsEditing(false);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);
    const closeQrModal = () => setQrModalOpen(false);

    const generateUserQRString = (user) => {
        return JSON.stringify({
            id: user.id,
            name: user.name,
            last_name: user.last_name,
            email: user.email,
            identification: user.identification
        });
    };

    const generateQRWithLogo = async (qrString, logoUrl, size = 512) => {
        return new Promise((resolve, reject) => {
            const canvas = document.createElement('canvas');
            canvas.width = size;
            canvas.height = size;
            
            QRCode.toCanvas(canvas, qrString, {
                errorCorrectionLevel: 'H',
                width: size,
                margin: 2,
                color: {
                    dark: '#000000',
                    light: '#ffffff'
                }
            }, (err) => {
                if (err) return reject(err);
                
                if (!logoUrl) {
                    return resolve(canvas.toDataURL('image/jpeg', 0.8));
                }
                
                const ctx = canvas.getContext('2d');
                const img = new Image();
                img.crossOrigin = 'Anonymous';
                img.onload = () => {
                    const logoSize = size * 0.25;
                    const x = (size - logoSize) / 2;
                    const y = (size - logoSize) / 2;
                    ctx.fillStyle = '#e2704d';
                    if (ctx.roundRect) {
                        ctx.beginPath();
                        ctx.roundRect(x - 8, y - 8, logoSize + 16, logoSize + 16, 12);
                        ctx.fill();
                    } else {
                        ctx.fillRect(x - 8, y - 8, logoSize + 16, logoSize + 16);
                    }
                    
                    ctx.drawImage(img, x, y, logoSize, logoSize);
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                };
                img.onerror = () => {
                    resolve(canvas.toDataURL('image/jpeg', 0.8));
                };
                img.src = logoUrl;
            });
        });
    };

    const handleViewQR = async (user) => {
        try {
            const qrString = generateUserQRString(user);
            const dataUrl = await generateQRWithLogo(qrString, visualConfig?.logoUrl, 512);
            setQrDataUrl(dataUrl);
            setQrUser(user);
            setQrModalOpen(true);
        } catch (err) {
            console.error("Error generating QR", err);
        }
    };

    const handleDownloadAllQRs = async () => {
        if (selectedUsers.length === 0) return;
        setIsDownloadingQRs(true);
        try {
            const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4', compress: true });
            let x = 20;
            let y = 20;
            const qrSize = 60;
            const margin = 20;
            
            const usersToPrint = users.filter(u => selectedUsers.includes(u.id));
            
            for (let i = 0; i < usersToPrint.length; i++) {
                const user = usersToPrint[i];
                const qrString = generateUserQRString(user);
                const qrDataUrl = await generateQRWithLogo(qrString, includeLogoInQR ? visualConfig?.logoUrl : null, 400);
                
                doc.addImage(qrDataUrl, 'JPEG', x, y, qrSize, qrSize);
                
                doc.setFontSize(12);
                doc.setTextColor(0, 0, 0);
                const fullName = `${user.name || ''} ${user.last_name || ''}`.trim() || 'Invitado';
                doc.text(fullName, x + (qrSize / 2), y + qrSize + 5, { align: 'center' });
                
                x += qrSize + margin;
                if (x + qrSize > 210 - margin + 10) {
                    x = 20;
                    y += qrSize + 20;
                }
                
                if (y + qrSize + 10 > 297 - margin + 10) {
                    if (i !== usersToPrint.length - 1) {
                        doc.addPage();
                        x = 20;
                        y = 20;
                    }
                }
            }
            doc.save('qrs_usuarios.pdf');
        } catch (err) {
            console.error("Error generating PDF", err);
        } finally {
            setIsDownloadingQRs(false);
        }
    };

    return (
        <div className="animate-fadeIn">
            <header className="flex flex-col md:flex-row justify-between items-start md:items-center mb-10 gap-6">
                <div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Gestión de Usuarios</h1>
                    <p className="text-slate-500 mt-2">Administra los roles y estados de los asistentes y staff.</p>
                </div>
                <div className="flex w-full md:w-auto gap-4">
                    <div className="relative flex-grow">
                        <span className="absolute inset-y-0 left-4 flex items-center text-slate-400">🔍</span>
                        <input 
                            type="text"
                            placeholder="Buscar por nombre, ID o email..."
                            className="bg-white border border-slate-100 pl-12 pr-6 py-3.5 rounded-2xl w-full focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-sm"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                        />
                    </div>
                    
                    <div className="flex items-center gap-3 bg-slate-50 border border-slate-100 px-4 py-3.5 rounded-2xl">
                        <label className="text-[10px] font-black text-slate-500 uppercase tracking-widest cursor-pointer flex items-center gap-2 select-none">
                            <input 
                                type="checkbox" 
                                className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer accent-indigo-600"
                                checked={includeLogoInQR}
                                onChange={(e) => setIncludeLogoInQR(e.target.checked)}
                            />
                            Incluir Logo
                        </label>
                    </div>

                    <button 
                        onClick={handleDownloadAllQRs}
                        disabled={isDownloadingQRs || selectedUsers.length === 0}
                        className={`bg-indigo-600 text-white px-8 py-3.5 rounded-2xl font-bold shadow-lg shadow-indigo-900/20 transition-all flex items-center whitespace-nowrap ${(isDownloadingQRs || selectedUsers.length === 0) ? 'opacity-70 cursor-not-allowed' : 'hover:bg-indigo-500'}`}
                    >
                        {isDownloadingQRs ? 'Generando PDF...' : `📥 Descargar QRs (${selectedUsers.length})`}
                    </button>
                    <button 
                        onClick={() => openModal()}
                        className="bg-blue-600 text-white px-8 py-3.5 rounded-2xl font-bold shadow-lg shadow-blue-900/20 hover:bg-blue-500 transition-all flex items-center whitespace-nowrap"
                    >
                        + Nuevo Usuario
                    </button>
                </div>
            </header>

            {isLoading ? (
                <div className="text-center py-20 bg-white rounded-[40px] border border-slate-100">
                    <div className="w-10 h-10 border-4 border-slate-100 border-t-blue-600 rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="text-slate-400 font-medium">Sincronizando base de datos...</p>
                </div>
            ) : users.length === 0 ? (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 p-20 text-center animate-fadeIn">
                    <div className="text-6xl mb-6 opacity-20">👥</div>
                    <h3 className="text-xl font-bold text-slate-800">No se encontraron usuarios</h3>
                    <p className="text-slate-400 mt-2">Intenta con otro término de búsqueda o registra uno nuevo.</p>
                </div>
            ) : (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="p-6 w-10">
                                    <input 
                                        type="checkbox" 
                                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                        checked={users.length > 0 && selectedUsers.length === users.length}
                                        onChange={(e) => {
                                            if (e.target.checked) setSelectedUsers(users.map(u => u.id));
                                            else setSelectedUsers([]);
                                        }}
                                    />
                                </th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Usuario</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Identificación</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Rol / Ciudad</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-center">Estado</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {users.map((u) => (
                                <tr key={u.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="p-6">
                                        <input 
                                            type="checkbox"
                                            className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                                            checked={selectedUsers.includes(u.id)}
                                            onChange={(e) => {
                                                if (e.target.checked) setSelectedUsers([...selectedUsers, u.id]);
                                                else setSelectedUsers(selectedUsers.filter(id => id !== u.id));
                                            }}
                                        />
                                    </td>
                                    <td className="p-6">
                                        <div className="flex items-center">
                                            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-400 font-black mr-4 border border-slate-200">
                                                {(u.name?.[0] || u.email?.[0] || '?').toUpperCase()}
                                            </div>
                                            <div className="max-w-[200px] overflow-hidden">
                                                <div className="font-bold text-slate-900 leading-snug truncate">
                                                    {u.name || (u.email ? u.email.split('@')[0] : 'Invitado')} {u.last_name || ''}
                                                </div>
                                                <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-0.5 truncate">{u.email}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="p-6">
                                        <div className="text-sm font-semibold text-slate-700">{u.identification || '---'}</div>
                                    </td>
                                    <td className="p-6">
                                        <span className={`text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
                                            u.role === 'admin' ? 'bg-purple-100 text-purple-600' : 
                                            u.role === 'staff' ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-600'
                                        }`}>
                                            {u.role}
                                        </span>
                                        <div className="text-[10px] text-slate-400 font-bold uppercase tracking-widest mt-2">{u.city || 'S/C'}, {u.country || 'S/P'}</div>
                                    </td>
                                    <td className="p-6 text-center">
                                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${u.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                            {u.status}
                                        </span>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className="flex justify-end space-x-2">
                                            <button 
                                                onClick={() => handleViewQR(u)}
                                                className="p-3 rounded-xl bg-slate-100 text-slate-600 hover:bg-indigo-600 hover:text-white transition-all shadow-sm"
                                                title="Ver QR"
                                            >
                                                📷
                                            </button>
                                            <button 
                                                onClick={() => openModal(u)}
                                                className="p-3 rounded-xl bg-slate-100 text-slate-600 hover:bg-blue-600 hover:text-white transition-all shadow-sm"
                                            >
                                                ✏️
                                            </button>
                                            <button 
                                                onClick={() => handleDelete(u.id)}
                                                className="p-3 rounded-xl bg-slate-100 text-red-400 hover:bg-red-500 hover:text-white transition-all shadow-sm"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-6 z-[100] animate-fadeIn">
                    <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-3xl overflow-hidden animate-slideUp">
                        <div className="bg-gradient-to-r from-blue-700 to-indigo-600 p-8 text-white relative">
                            <h2 className="text-3xl font-black">{isEditing ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>
                            <p className="text-blue-100 mt-1">Completa los datos del perfil.</p>
                            <button onClick={closeModal} className="absolute top-8 right-8 text-white/50 hover:text-white text-2xl">✕</button>
                        </div>
                        <form onSubmit={handleSave} className="p-10 grid grid-cols-2 gap-6">
                            {/* Nombres */}
                            {regFields.name !== false && (
                                <div className={regFields.last_name === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Nombres</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.name}
                                        onChange={e => setCurrentUser({...currentUser, name: e.target.value})}
                                        required
                                    />
                                </div>
                            )}

                            {/* Apellidos */}
                            {regFields.last_name !== false && (
                                <div className={regFields.name === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Apellidos</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.last_name}
                                        onChange={e => setCurrentUser({...currentUser, last_name: e.target.value})}
                                        required
                                    />
                                </div>
                            )}

                            {/* Email */}
                            <div className={regFields.identification === false ? 'col-span-2' : 'col-span-1'}>
                                <label className="block text-slate-700 text-sm font-bold mb-2">Email</label>
                                <input 
                                    type="email"
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                    value={currentUser.email}
                                    onChange={e => setCurrentUser({...currentUser, email: e.target.value})}
                                    required
                                />
                            </div>

                            {/* Identificación */}
                            {regFields.identification !== false && (
                                <div className="col-span-1">
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Identificación (Cédula)</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.identification}
                                        onChange={e => setCurrentUser({...currentUser, identification: e.target.value})}
                                        required
                                    />
                                </div>
                            )}

                            {/* Ciudad */}
                            {regFields.city !== false && (
                                <div className={regFields.country === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Ciudad</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.city}
                                        onChange={e => setCurrentUser({...currentUser, city: e.target.value})}
                                    />
                                </div>
                            )}

                            {/* País */}
                            {regFields.country !== false && (
                                <div className={regFields.city === false ? 'col-span-2' : 'col-span-1'}>
                                    <label className="block text-slate-700 text-sm font-bold mb-2">País</label>
                                    <input 
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.country}
                                        onChange={e => setCurrentUser({...currentUser, country: e.target.value})}
                                    />
                                </div>
                            )}

                            {/* Pass/Role/Status - Adaptive */}
                            <div className="col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Rol</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner appearance-none"
                                    value={currentUser.role}
                                    onChange={e => setCurrentUser({...currentUser, role: e.target.value})}
                                >
                                    <option value="user">Usuario</option>
                                    <option value="staff">Staff</option>
                                    <option value="admin">Administrador</option>
                                </select>
                            </div>
                            <div className="col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Estado</label>
                                <select 
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner appearance-none"
                                    value={currentUser.status}
                                    onChange={e => setCurrentUser({...currentUser, status: e.target.value})}
                                >
                                    <option value="active">Activo</option>
                                    <option value="inactive">Inactivo</option>
                                </select>
                            </div>
                            {!isEditing && (
                                <div className="col-span-2">
                                    <label className="block text-slate-700 text-sm font-bold mb-2">Contraseña</label>
                                    <input 
                                        type="password"
                                        className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-blue-100 transition-all shadow-inner"
                                        value={currentUser.password || ''}
                                        onChange={e => setCurrentUser({...currentUser, password: e.target.value})}
                                        required
                                        placeholder="Min. 6 caracteres"
                                    />
                                </div>
                            )}
                            <div className="col-span-2 pt-6 flex justify-end space-x-4">
                                <button type="button" onClick={closeModal} className="px-8 py-4 text-slate-500 font-bold hover:text-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="bg-blue-600 text-white px-12 py-4 rounded-3xl font-black shadow-xl shadow-blue-900/20 hover:bg-blue-500 transition-all active:scale-95">
                                    {isEditing ? 'Guardar Cambios' : 'Registrar Usuario'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
            {/* QR Modal */}
            {qrModalOpen && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-6 z-[110] animate-fadeIn">
                    <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-sm overflow-hidden animate-slideUp text-center pb-8">
                        <div className="bg-gradient-to-r from-indigo-700 to-purple-600 p-6 text-white relative">
                            <h2 className="text-xl font-black">Código QR</h2>
                            <p className="text-indigo-100 mt-1 text-sm">{qrUser?.name} {qrUser?.last_name}</p>
                            <button onClick={closeQrModal} className="absolute top-6 right-6 text-white/50 hover:text-white text-xl">✕</button>
                        </div>
                        <div className="p-8 flex justify-center">
                            {qrDataUrl && <img src={qrDataUrl} alt="QR Code" className="w-64 h-64 object-contain shadow-sm border border-slate-100 rounded-2xl p-2" />}
                        </div>
                        <button 
                            onClick={() => {
                                const link = document.createElement('a');
                                link.href = qrDataUrl;
                                link.download = `qr_${qrUser?.name || 'usuario'}.png`;
                                link.click();
                            }}
                            className="bg-indigo-600 text-white px-8 py-3 rounded-2xl font-bold shadow-lg shadow-indigo-900/20 hover:bg-indigo-500 transition-all"
                        >
                            Descargar QR
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Users;
